import { InputAdornment, Typography } from '@material-ui/core';
import { Visibility, VisibilityOff } from '@material-ui/icons';
import React, { useState } from 'react';
import NumberFormat from 'react-number-format';

import { useTranslation } from '../../hooks/useTranslation';
import DelayedTextMask from '../delayed-text-mask/DelayedTextMask';
import TextField from '../text-field';
import { TextFieldProps } from '../text-field/TextField';

interface NumberFormatCustomProps {
  inputRef: (instance: NumberFormat | null) => void;
  onChange: (event: { target: { value: string } }) => void;
}

const SSNFormat = (props: NumberFormatCustomProps | any) => {
  const { inputRef, onChange, ...other } = props;

  return (
    <NumberFormat
      data-testid={'ssn-input'}
      {...other}
      getInputRef={inputRef}
      onPaste={() => {
        window.getSelection()?.collapseToStart();
      }}
      onValueChange={values => {
        if (values.value !== other.value) {
          onChange({
            target: {
              value: values.value,
              name: props.name,
            },
          });
        }
      }}
      type={'text'}
      format="###-##-####"
      mask="_"
      allowNegative={false}
      isNumericString
    />
  );
};

const SSNTextField = React.forwardRef(
  (props: TextFieldProps | any, ref?: React.Ref<HTMLInputElement>) => {
    const { t } = useTranslation();
    const [ssnIsVisible, toggleSSNVisibility] = useState<boolean>(false);
    const [autoFocus, setAutoFocus] = useState<boolean>(false);
    const [shrink, setShrink] = useState<boolean>(false);
    const handleInputAdornmentClick = () => {
      toggleSSNVisibility(!ssnIsVisible);
      setAutoFocus(true);
    };
    const handleFocus = (e: any) => {
      setShrink(true);
    };
    const handleBlur = (e: any) => {
      props?.onBlur();
      if (e.target.value) {
        setShrink(true);
      } else {
        setShrink(false);
      }
    };

    return (
      <TextField
        label={props.label || t('SOCIAL_SECURITY_NUMBER')}
        onChange={props?.onChange}
        ref={ref}
        InputProps={{
          inputComponent: ssnIsVisible ? SSNFormat : DelayedTextMask,
          maxLength: 9,
          endAdornment: (
            <InputAdornment
              data-testid={'ssn-visibility'}
              onClick={handleInputAdornmentClick}
              style={{ cursor: 'pointer' }}
              position={'end'}
            >
              <Typography variant="body2" color="primary">
                {ssnIsVisible ? (
                  <Visibility data-testid="visibility-on" />
                ) : (
                  <VisibilityOff data-testid="visibility-off" />
                )}
              </Typography>
            </InputAdornment>
          ),
        }}
        inputProps={{
          ...props.inputProps,
          autoFocus,
          ...(!ssnIsVisible && {
            'data-testid': 'ssn-input',
            inputmode: 'numeric',
            numbersOnly: true,
            maxLength: 9,
          }),
        }}
        inputRef={props.inputRef}
        name={props.name || 'ssn'}
        margin={'normal'}
        style={props.style || { width: '100%', marginTop: 0 }}
        type={'num'}
        {...props}
        defaultValue={props.defaultValue}
        InputLabelProps={{ shrink: Boolean(props?.value) || shrink }}
        onFocus={handleFocus}
        onBlur={handleBlur}
      />
    );
  },
);

export default SSNTextField;
